import { cva, type VariantProps } from 'class-variance-authority';

import { BRAND_HEX } from '../../styles/brand-colors';

/**
 * SR-button container variants — applied on top of `<Button variant="primary">`
 * via `className`. Because we compose through the existing Button, we only
 * need to redeclare the classes that visually differ from primary:
 */
export const srContainerVariants = cva(
  // `min-w-0`: lets the chip shrink below its label's intrinsic width when the
  // caller puts it in a constrained row (`flex-1` siblings) — that shrink is
  // what makes the label's ellipsis + marquee clamp kick in instead of the
  // chip growing or the label wrapping.
  'flex-col h-auto min-w-0 ' +
    '!shadow-[0px_2px_8px_0px_rgba(0,0,0,0.06)] enabled:active:!shadow-none',
  {
    variants: {
      action: {
        bad:
          '!bg-orange-100 border-orange-200 ' +
          'enabled:hover:!bg-orange-200 enabled:active:!bg-orange-200 ' +
          'enabled:hover:!border-orange-300 enabled:active:!border-orange-300 ' +
          'web:focus-visible:!ring-orange-600', // orange: bg 100→200, border 200→300, ring 600
        hard:
          '!bg-amber-100 border-amber-200 ' +
          'enabled:hover:!bg-amber-200 enabled:active:!bg-amber-200 ' +
          'enabled:hover:!border-amber-300 enabled:active:!border-amber-300 ' +
          'web:focus-visible:!ring-amber-600', // amber: bg 100→200, border 200→300, ring 600
        good:
          '!bg-lime-100 border-lime-200 ' +
          'enabled:hover:!bg-lime-200 enabled:active:!bg-lime-200 ' +
          'enabled:hover:!border-lime-300 enabled:active:!border-lime-300 ' +
          'web:focus-visible:!ring-lime-600', // lime: bg 100→200, border 200→300, ring 600
        easy:
          '!bg-green-100 border-green-200 ' +
          'enabled:hover:!bg-green-200 enabled:active:!bg-green-200 ' +
          'enabled:hover:!border-green-300 enabled:active:!border-green-300 ' +
          'web:focus-visible:!ring-green-600', // green: bg 100→200, border 200→300, ring 600
      },
      layout: {
        mobile:
          'border-[1.5px] border-b-4 gap-1 px-[18px] pt-[10.5px] pb-3 !rounded-[20px] ' +
          'enabled:active:!border-b-[1.5px] enabled:active:!pb-[14.5px]',
        desktop:
          'border border-b-2 gap-0 px-[13px] pt-[10px] pb-[10px] ' +
          'rounded-tl-[21px] rounded-tr-[21px] rounded-bl-[22px] rounded-br-[22px] ' +
          'enabled:active:!border-b enabled:active:!pb-[11px]',
      },
    },
    defaultVariants: {
      action: 'easy',
      layout: 'mobile',
    },
  },
);

/**
 * Label typography per layout.
 */
export const srLabelVariants = cva('whitespace-nowrap font-bold native:font-mona-bold', {
  variants: {
    layout: {
      // 15px is intentionally off-scale — sized to fit 4 chips on small
      // phones (see the doc comment above), left arbitrary.
      mobile: 'text-[15px] !leading-[normal]',
      desktop: 'text-l1',
    },
  },
  defaultVariants: { layout: 'mobile' },
});

/**
 * Sub-label typography (desktop layout only).
 */
export const srSubLabelClass =
  'whitespace-nowrap font-medium native:font-mona-medium text-b3 opacity-60';

/**
 * Per-action sub-label ("show it in …") colour.
 */
export const srSubLabelColorClass: Record<
  NonNullable<VariantProps<typeof srContainerVariants>['action']>,
  string
> = {
  bad: 'text-orange-500',
  hard: 'text-amber-600',
  good: 'text-lime-600',
  easy: 'text-green-600',
};

/**
 * Per-action LABEL colour.
 */
export const srLabelColorClass: Record<
  NonNullable<VariantProps<typeof srContainerVariants>['action']>,
  string
> = {
  bad: 'text-orange-500',
  hard: 'text-amber-600',
  good: 'text-lime-600',
  easy: 'text-green-600',
};

/**
 * Per-action ICON colours — LIGHT mode.
 */
export const srIconColors: Record<
  NonNullable<VariantProps<typeof srContainerVariants>['action']>,
  { fill: string; stroke: string }
> = {
  bad: { fill: BRAND_HEX.orange['400'], stroke: BRAND_HEX.orange['800'] },
  hard: { fill: BRAND_HEX.amber['400'], stroke: BRAND_HEX.amber['800'] },
  good: { fill: BRAND_HEX.lime['400'], stroke: BRAND_HEX.lime['800'] },
  easy: { fill: BRAND_HEX.green['400'], stroke: BRAND_HEX.green['800'] },
};

/**
 * Per-action ICON colour — DARK mode.
 */
export const srIconColorDark: Record<
  NonNullable<VariantProps<typeof srContainerVariants>['action']>,
  string
> = {
  bad: BRAND_HEX.orange['500'], // #ff6900 — dark orange-500 (= light, fixed point)
  hard: BRAND_HEX.amber['400'], // #ffb900 — dark amber-600
  good: BRAND_HEX.lime['400'], // #9ae600 — dark lime-600
  easy: BRAND_HEX.green['400'], // #05df72 — dark green-600
};

export type SrContainerVariantProps = VariantProps<typeof srContainerVariants>;
